import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Check, X, CreditCard, Shield, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import BetaBanner from '@/components/BetaBanner';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Payment Success Page - polls for status and shows confirmation
export const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const athleteId = searchParams.get('athlete_id');
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 10;

  const pollStatus = useCallback(async () => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    try {
      const response = await axios.get(`${API}/api/athlete-passes/checkout/status/${sessionId}`);
      const { payment_status, status: sessionStatus } = response.data;

      if (payment_status === 'paid') {
        setStatus('success');
        toast.success('Athlete Pass activated successfully!');
        return;
      } else if (sessionStatus === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling if not yet paid
      if (attempts < maxAttempts) {
        setAttempts(prev => prev + 1);
      } else {
        setStatus('timeout');
      }
    } catch (error) {
      console.error('Status check error:', error);
      if (attempts >= maxAttempts) {
        setStatus('error');
      } else {
        setAttempts(prev => prev + 1);
      }
    }
  }, [sessionId, attempts]);

  useEffect(() => {
    if (status === 'checking' && attempts < maxAttempts) {
      const timer = setTimeout(pollStatus, 2000);
      return () => clearTimeout(timer);
    }
  }, [status, attempts, pollStatus]);

  useEffect(() => {
    // Initial poll
    if (sessionId) {
      pollStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      {/* Founding Beta Banner */}
      <div className="fixed top-0 left-0 right-0 z-50">
        <BetaBanner />
      </div>

      <div className="bg-card border border-border p-8 rounded-sm max-w-md w-full text-center">
        {status === 'checking' && (
          <>
            <Loader2 className="mx-auto text-primary mb-4 animate-spin" size={48} />
            <h1 className="font-heading text-2xl uppercase mb-2">Processing Payment</h1>
            <p className="text-muted-foreground">Verifying your payment with Stripe...</p>
            <p className="text-xs text-muted-foreground mt-2">Attempt {attempts}/{maxAttempts}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
              <Check className="text-green-500" size={32} />
            </div>
            <h1 className="font-heading text-2xl uppercase mb-2 text-green-500">Payment Successful!</h1>
            <p className="text-muted-foreground mb-6">
              Your Athlete Pass has been activated. You now have full access to track your development journey.
            </p>
            <div className="space-y-3">
              <Button asChild className="w-full bg-primary text-black hover:bg-primary/90">
                <Link to="/dashboard">Go to Dashboard</Link>
              </Button>
              <Button asChild variant="outline" className="w-full">
                <Link to="/">Back to Home</Link>
              </Button>
            </div>
          </>
        )}

        {status === 'expired' && (
          <>
            <div className="w-16 h-16 rounded-full bg-yellow-500/20 flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="text-yellow-500" size={32} />
            </div>
            <h1 className="font-heading text-2xl uppercase mb-2 text-yellow-500">Session Expired</h1>
            <p className="text-muted-foreground mb-6">
              Your payment session has expired. Please try again.
            </p>
            <Button asChild className="w-full bg-primary text-black hover:bg-primary/90">
              <Link to="/">Back to Home</Link>
            </Button>
          </>
        )}

        {(status === 'error' || status === 'timeout') && (
          <>
            <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
              <X className="text-red-500" size={32} />
            </div>
            <h1 className="font-heading text-2xl uppercase mb-2 text-red-500">Verification Issue</h1>
            <p className="text-muted-foreground mb-6">
              We couldn't verify your payment status. If you completed the payment, your pass will be activated shortly via webhook.
            </p>
            <div className="space-y-3">
              <Button asChild className="w-full bg-primary text-black hover:bg-primary/90">
                <Link to="/dashboard">Check Dashboard</Link>
              </Button>
              <Button asChild variant="outline" className="w-full">
                <Link to="/">Back to Home</Link>
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Payment Cancel Page
export const PaymentCancel = () => {
  const [searchParams] = useSearchParams();
  const athleteId = searchParams.get('athlete_id');

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      {/* Founding Beta Banner */}
      <div className="fixed top-0 left-0 right-0 z-50">
        <BetaBanner />
      </div>

      <div className="bg-card border border-border p-8 rounded-sm max-w-md w-full text-center">
        <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
          <X className="text-red-500" size={32} />
        </div>
        <h1 className="font-heading text-2xl uppercase mb-2">Payment Cancelled</h1>
        <p className="text-muted-foreground mb-6">
          Your payment was cancelled. No charges were made. You can try again whenever you're ready.
        </p>
        <div className="space-y-3">
          <Button asChild className="w-full bg-primary text-black hover:bg-primary/90">
            <Link to="/">Back to Home</Link>
          </Button>
        </div>
      </div>
    </div>
  );
};

// Mock Payment Page (for beta testing - redirects to Stripe test checkout)
export const MockPaymentPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);
  const [useStripe, setUseStripe] = useState(true); // Default to Stripe checkout
  
  const athleteId = searchParams.get('athlete_id') || window.location.pathname.split('/').pop();
  const coachId = searchParams.get('coach');
  const amount = searchParams.get('amount') || '9.99';
  const athleteName = searchParams.get('name') || 'Your Athlete';

  // Method 1: Use Stripe Checkout (Test Mode)
  const handleStripeCheckout = async () => {
    setProcessing(true);
    
    try {
      // Get token from localStorage
      const token = localStorage.getItem('token');
      const originUrl = window.location.origin;
      
      const response = await axios.post(
        `${API}/api/athlete-passes/checkout`,
        {
          athlete_id: athleteId,
          origin_url: originUrl,
          pass_type: 'standard'
        },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      
      // Redirect to Stripe Checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Stripe checkout error:', error);
      
      // If not authenticated as coach, fall back to mock payment
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Coach authentication required for Stripe checkout. Using mock payment.');
        setUseStripe(false);
      } else {
        toast.error('Failed to start checkout. Please try again.');
      }
      setProcessing(false);
    }
  };

  // Method 2: Mock Payment (for testing without coach auth)
  const handleMockPayment = async () => {
    setProcessing(true);
    
    try {
      // Simulate payment processing delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Call backend to confirm payment and activate pass
      await axios.post(`${API}/api/athlete-passes/confirm-payment/${athleteId}`);
      
      toast.success('Payment confirmed! Athlete Pass activated.');
      navigate(`/payment/success?athlete_id=${athleteId}&name=${encodeURIComponent(athleteName)}`);
    } catch (error) {
      console.error('Payment confirmation error:', error);
      toast.error('Payment simulation failed. Please try again.');
      setProcessing(false);
    }
  };

  const handlePayment = () => {
    if (useStripe) {
      handleStripeCheckout();
    } else {
      handleMockPayment();
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      {/* Founding Beta Banner */}
      <div className="fixed top-0 left-0 right-0 z-50">
        <BetaBanner />
      </div>

      <div className="bg-card border border-border p-8 rounded-sm max-w-md w-full">
        <div className="text-center mb-6">
          <CreditCard className="mx-auto text-primary mb-4" size={48} />
          <h1 className="font-heading text-2xl uppercase mb-2">Athlete Pass</h1>
          <p className="text-muted-foreground">Subscription for <span className="text-primary font-medium">{decodeURIComponent(athleteName)}</span></p>
        </div>

        <div className="bg-muted/30 border border-border/40 rounded-sm p-4 mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-muted-foreground">Athlete Pass (Monthly)</span>
            <span className="font-bold text-xl">${amount}</span>
          </div>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>✓ Full progress tracking</li>
            <li>✓ Training plans access</li>
            <li>✓ Video analysis</li>
            <li>✓ Coach collaboration</li>
          </ul>
        </div>

        {/* Payment Method Toggle */}
        <div className="mb-4 p-3 bg-muted/20 border border-border/40 rounded-sm">
          <p className="text-xs text-muted-foreground mb-2">Payment Method (Test Mode)</p>
          <div className="flex gap-2">
            <button
              onClick={() => setUseStripe(true)}
              className={`flex-1 py-2 px-3 text-xs rounded-sm transition-colors ${
                useStripe ? 'bg-primary text-black' : 'bg-muted hover:bg-muted/80'
              }`}
            >
              Stripe Checkout
            </button>
            <button
              onClick={() => setUseStripe(false)}
              className={`flex-1 py-2 px-3 text-xs rounded-sm transition-colors ${
                !useStripe ? 'bg-primary text-black' : 'bg-muted hover:bg-muted/80'
              }`}
            >
              Mock Payment
            </button>
          </div>
          {useStripe && (
            <p className="text-xs text-amber-400 mt-2">
              Use test card: 4242 4242 4242 4242
            </p>
          )}
        </div>

        <div className="space-y-4">
          <Button 
            onClick={handlePayment}
            disabled={processing}
            className="w-full bg-primary text-black hover:bg-primary/90 h-12"
            data-testid="payment-btn"
          >
            {processing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {useStripe ? 'Redirecting to Stripe...' : 'Processing...'}
              </>
            ) : (
              `Pay $${amount}/month`
            )}
          </Button>

          <Button asChild variant="outline" className="w-full">
            <Link to="/">Cancel</Link>
          </Button>
        </div>

        <div className="mt-6 flex items-center justify-center gap-2 text-xs text-muted-foreground">
          <Shield size={14} />
          <span>Secure payment powered by Stripe (Test Mode)</span>
        </div>
      </div>
    </div>
  );
};

export default MockPaymentPage;
